import { getLexiconEntry } from '@tunasong/music-lib'
import { Button, useNavigate } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { spokenText } from '../theory-copy/spoken-text.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy, useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import styles from './related-tools.module.css'

export interface RelatedTool {
  /** Internal route to navigate to (e.g., '/quiz?key=C'). */
  targetRoute: string
  /** Lexicon id whose copy is used for the button label + tooltip. */
  lexiconId: string
}

export interface RelatedToolsProps {
  tools: readonly RelatedTool[]
}

interface RelatedToolLinkProps {
  tool: RelatedTool
}

const RelatedToolLink: FC<RelatedToolLinkProps> = ({ tool }) => {
  const copy = useTheoryCopy(tool.lexiconId)
  const navigate = useNavigate()

  if (!copy) {
    return null
  }

  const handleClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    // Left-click handled in SPA; middle/right-click still use the href.
    e.preventDefault()
    void navigate(tool.targetRoute)
  }

  return (
    <Button
      variant="text"
      size="small"
      href={tool.targetRoute}
      onClick={handleClick}
      title={spokenText(copy.body)}
      className={styles.link}
    >
      <TheoryGlyphs>{copy.displayNameShort}</TheoryGlyphs>
    </Button>
  )
}

/**
 * Shared "Related tools" footer block for the three standalone Music-Theory
 * Learning routes (Mode Lab, Function Quiz, Reharm Lab). Renders an
 * accessible region with one link `<Button>` per tool, hiding individual
 * items whose lexicon entry is missing (or whose user has
 * `theoryLevel = 'off'`).
 *
 * If the resulting visible-item count is zero (all items hidden, or empty
 * tools list, or `theoryLevel = 'off'`), the entire section is omitted from
 * the DOM.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §4
 */
export const RelatedTools: FC<RelatedToolsProps> = ({ tools }) => {
  const level = useTheoryLevel()
  if (level === 'off') {
    return null
  }
  if (tools.length === 0) {
    return null
  }
  // Pre-filter by lexicon-entry existence so the "all unknown" case can
  // short-circuit the entire section. `getLexiconEntry` is a pure function
  // (not a hook) — safe to call in the parent.
  const visibleTools = tools.filter(tool => getLexiconEntry(tool.lexiconId) !== null)
  if (visibleTools.length === 0) {
    return null
  }
  return (
    <section className={styles.section} aria-label="Related tools">
      <h2 className={styles.heading}>Related tools</h2>
      <div className={styles.links}>
        {visibleTools.map(tool => (
          <RelatedToolLink key={`${tool.targetRoute}::${tool.lexiconId}`} tool={tool} />
        ))}
      </div>
    </section>
  )
}
